import React, { useState, useEffect, useCallback } from 'react';
import supabase from '../../supabaseClient';
import { TelegramService } from '../../services/telegramService';
import PremiumGate from '../PremiumGate';

interface AdvancedMealAnalysisProps {
  selectedUserId: string;
  clientName: string;
  onUpgrade?: () => void;
}

interface DietGoals {
  target_calories: number;
  protein_target: number;
  carbs_target: number;
  fat_target: number;
}

type MacroKey = 'calories' | 'protein' | 'carbs' | 'fat';

const OVER_TARGET_THRESHOLD = 120;

const calculateProgress = (current: number, target: number) => {
  if (!target || target === 0) return 0;
  return Math.round((current / target) * 100);
};

const getProgressBarColor = (progress: number, macro: MacroKey) => {
  if (macro === 'protein') {
    return progress >= 90 ? '#22c55e' : '#4A90E2';
  }
  if (progress > OVER_TARGET_THRESHOLD) return '#ef4444';
  if (progress > 100) return '#eab308';
  if (progress >= 80) return '#22c55e';
  return '#4A90E2';
};

const getProgressPercentColor = (progress: number, macro: MacroKey) => {
  if (macro === 'protein') {
    return progress >= 90 ? '#22c55e' : '#5A5A5A';
  }
  if (progress > OVER_TARGET_THRESHOLD) return '#ef4444';
  if (progress > 100) return '#eab308';
  return '#5A5A5A';
};

interface RecommendationResult {
  recommendation: string;
  foods: string[];
  caloriesRemaining: number;
  proteinRemaining: number;
  overTargets: { label: string; percent: number }[];
}

function generateRecommendation(
  clientName: string,
  todayStats: { calories: number; protein: number; carbs: number; fat: number },
  goals: DietGoals
): RecommendationResult {
  const name = clientName.trim() || 'there';
  const caloriesProgress = calculateProgress(todayStats.calories, goals.target_calories);
  const proteinProgress = calculateProgress(todayStats.protein, goals.protein_target);
  const carbsProgress = calculateProgress(todayStats.carbs, goals.carbs_target);
  const fatProgress = calculateProgress(todayStats.fat, goals.fat_target);

  const caloriesRemaining = goals.target_calories - todayStats.calories;
  const proteinRemaining = goals.protein_target - todayStats.protein;

  const overTargets: { label: string; percent: number }[] = [];
  if (caloriesProgress > OVER_TARGET_THRESHOLD) {
    overTargets.push({ label: 'calories', percent: caloriesProgress });
  }
  if (carbsProgress > OVER_TARGET_THRESHOLD) {
    overTargets.push({ label: 'carbs', percent: carbsProgress });
  }
  if (fatProgress > OVER_TARGET_THRESHOLD) {
    overTargets.push({ label: 'fat', percent: fatProgress });
  }

  let recommendation = '';
  let foods: string[] = [];

  if (overTargets.length > 0) {
    const overDescriptions = overTargets.map((item) => {
      const label = item.label === 'calories' ? 'calories' : item.label === 'carbs' ? 'carbs' : 'fat';
      return `${label} (${item.percent}%)`;
    });

    if (overTargets.length === 1) {
      const { label, percent } = overTargets[0];
      const macroLabel = label === 'calories' ? 'calorie' : label === 'carbs' ? 'carb' : 'fat';
      recommendation = `${name}, you've gone over your daily ${macroLabel} target today — you're at about ${percent}% of your allowance. It happens! Keep the rest of today lighter if you can, stay hydrated, and we'll hit it fresh tomorrow.`;
    } else {
      recommendation = `${name}, you're over your targets today on ${overDescriptions.join(' and ')}. No worries — one day doesn't define your progress. Try to keep things lighter for the rest of the day, and we'll reset strong tomorrow.`;
    }
  } else if (caloriesProgress > 100 && proteinProgress < 70) {
    recommendation = `${name}, you're close to your calorie target but still a bit low on protein. Try adding some of these high-protein, lower-calorie options:`;
    foods = ['Grilled chicken breast', 'White fish (cod, tilapia)', 'Egg whites', 'Non-fat Greek yogurt', 'Protein shake (isolate)'];
  } else if (caloriesProgress < 50 && proteinProgress < 50) {
    recommendation = `${name}, you've still got room to hit your targets today. Here are some balanced, nutrient-dense meal ideas:`;
    foods = ['Grilled chicken with brown rice', 'Salmon with quinoa', 'Lean beef with sweet potato', 'Turkey breast with vegetables'];
  } else if (proteinProgress >= 90 && carbsProgress < 60) {
    recommendation = `${name}, great work on protein today! You could use a few more carbs for energy — try adding:`;
    foods = ['Brown rice', 'Oatmeal', 'Sweet potato', 'Whole grain pasta', 'Quinoa'];
  } else if (proteinProgress < 70) {
    recommendation = `${name}, you're running low on protein today. Here are some easy options to help you close the gap:`;
    foods = ['Chicken breast', 'Turkey', 'Tuna', 'Greek yogurt', 'Cottage cheese', 'Protein shake'];
  } else if (caloriesProgress >= 80 && caloriesProgress <= 100 && proteinProgress >= 80) {
    recommendation = `${name}, you're on track today — nice work! Keep going with your current eating pattern.`;
  } else if (caloriesProgress > 100 && caloriesProgress <= OVER_TARGET_THRESHOLD) {
    recommendation = `${name}, you're slightly over your calorie target today (${caloriesProgress}%). Not the end of the world — try to keep the rest of the day lighter and we'll bounce back tomorrow.`;
  } else {
    recommendation = `${name}, you're making solid progress today. Stay consistent with your meals and keep logging — you're doing great.`;
  }

  return { recommendation, foods, caloriesRemaining, proteinRemaining, overTargets };
}

export default function AdvancedMealAnalysis({ selectedUserId, clientName, onUpgrade }: AdvancedMealAnalysisProps) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [todayStats, setTodayStats] = useState({
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    mealCount: 0,
  });
  const [goals, setGoals] = useState<DietGoals | null>(null);
  const [trainerId, setTrainerId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!selectedUserId) return;

    try {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (user) setTrainerId(user.id);

      const today = new Date();
      const startOfDay = new Date(today.toISOString().split('T')[0] + 'T00:00:00.000Z');

      const { data: meals, error: mealsError } = await supabase
        .from('Mastersheet')
        .select('calories, protein_g, carbs_g, fat_g')
        .eq('telegram_user_id', selectedUserId)
        .gte('created_at', startOfDay.toISOString());

      if (mealsError) throw mealsError;

      const totals = (meals || []).reduce(
        (acc, meal) => ({
          calories: acc.calories + (meal.calories || 0),
          protein: acc.protein + (meal.protein_g || 0),
          carbs: acc.carbs + (meal.carbs_g || 0),
          fat: acc.fat + (meal.fat_g || 0),
          mealCount: acc.mealCount + 1,
        }),
        { calories: 0, protein: 0, carbs: 0, fat: 0, mealCount: 0 }
      );

      setTodayStats(totals);

      const { data: goalsData, error: goalsError } = await supabase
        .from('client_diet_goals')
        .select('target_calories, protein_target, carbs_target, fat_target')
        .eq('telegram_user_id', selectedUserId)
        .single();

      if (!goalsError && goalsData) {
        setGoals(goalsData);
      } else {
        setGoals(null);
      }
    } catch (error) {
      console.error('Error fetching meal analysis data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedUserId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const analysis = goals ? generateRecommendation(clientName, todayStats, goals) : null;

  const sendRecommendation = async () => {
    if (!analysis || !trainerId) {
      alert('Unable to generate recommendation. Client may not have diet goals set.');
      return;
    }

    setSending(true);
    try {
      let message = `🎯 <b>Today's Check-in</b>\n\n`;
      message += `${analysis.recommendation}\n\n`;

      if (analysis.foods.length > 0) {
        message += `<b>Ideas to try:</b>\n`;
        analysis.foods.forEach((food) => {
          message += `• ${food}\n`;
        });
        message += `\n`;
      }

      if (analysis.proteinRemaining > 0) {
        message += `Protein remaining today: ${Math.round(analysis.proteinRemaining)}g\n`;
      }
      if (analysis.caloriesRemaining > 0) {
        message += `Calories remaining today: ${Math.round(analysis.caloriesRemaining)}\n`;
      }

      const success = await TelegramService.sendMessage(selectedUserId, message);

      if (success) {
        await supabase.from('client_messages').insert({
          trainer_id: trainerId,
          client_telegram_id: selectedUserId,
          message_type: 'meal_recommendation',
          message_content: message,
          status: 'sent',
        });

        alert(`Your meal recommendation has been sent to ${clientName} via Telegram.`);
      } else {
        alert('Failed to send recommendation via Telegram.');
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to send recommendation';
      alert(msg);
    } finally {
      setSending(false);
    }
  };

  const progressItems: {
    key: MacroKey;
    label: string;
    current: number;
    target: number;
    unit: string;
  }[] = goals
    ? [
        { key: 'calories', label: 'Calories', current: todayStats.calories, target: goals.target_calories, unit: '' },
        { key: 'protein', label: 'Protein', current: todayStats.protein, target: goals.protein_target, unit: 'g' },
        { key: 'carbs', label: 'Carbs', current: todayStats.carbs, target: goals.carbs_target, unit: 'g' },
        { key: 'fat', label: 'Fat', current: todayStats.fat, target: goals.fat_target, unit: 'g' },
      ]
    : [];

  return (
    <PremiumGate
      featureName="Advanced Meal Analysis"
      description="Today's macro progress and smart coaching recommendations sent via Telegram."
      onUpgrade={onUpgrade}
    >
      {loading ? (
        <div className="text-center py-8 text-[#2d2d2d]">Loading today's analysis...</div>
      ) : (
        <div className="space-y-6">
          <div>
            <p className="text-sm text-[#666]">For {clientName}</p>
            {todayStats.mealCount > 0 && (
              <p className="text-xs text-[#888] mt-1">{todayStats.mealCount} meal{todayStats.mealCount !== 1 ? 's' : ''} logged today</p>
            )}
          </div>

          <div>
            <h4 className="text-md font-semibold text-[#1f1f1f] mb-4">📊 Today's Progress</h4>
            {goals ? (
              <div className="space-y-4">
                {progressItems.map((item) => {
                  const progress = calculateProgress(item.current, item.target);
                  const barColor = getProgressBarColor(progress, item.key);
                  const percentColor = getProgressPercentColor(progress, item.key);

                  return (
                    <div key={item.key}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-semibold text-[#1f1f1f]">{item.label}</span>
                        <span className="text-sm text-[#666]">
                          {Math.round(item.current)}{item.unit} / {item.target}{item.unit}
                        </span>
                      </div>
                      <div className="h-3 bg-[#E0E0E0] rounded-full overflow-hidden mb-1">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${Math.min(progress, 100)}%`, backgroundColor: barColor }}
                        />
                      </div>
                      <p className="text-xs text-right" style={{ color: percentColor }}>
                        {progress}%
                        {progress > OVER_TARGET_THRESHOLD && item.key !== 'protein' ? ' — over target' : ''}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-[#888] italic">
                No diet goals set for this client. Set goals to see progress and recommendations.
              </p>
            )}
          </div>

          {analysis && (
            <div>
              <h4 className="text-md font-semibold text-[#1f1f1f] mb-1">🎯 Smart Recommendation</h4>
              <p className="text-xs text-[#888] mb-3">Preview of message to {clientName}</p>

              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0] mb-4">
                <p className="text-sm text-[#2C2C2C] leading-relaxed mb-4">{analysis.recommendation}</p>

                {analysis.foods.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-[#1f1f1f] mb-2">Ideas to try:</p>
                    <ul className="space-y-1">
                      {analysis.foods.map((food) => (
                        <li key={food} className="text-sm text-[#666]">• {food}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {(analysis.proteinRemaining > 0 || analysis.caloriesRemaining > 0) && (
                  <div className="border-t border-[#E0E0E0] pt-3 space-y-1">
                    {analysis.proteinRemaining > 0 && (
                      <p className="text-xs text-[#666]">
                        Protein remaining: <span className="font-semibold text-[#4A90E2]">{Math.round(analysis.proteinRemaining)}g</span>
                      </p>
                    )}
                    {analysis.caloriesRemaining > 0 && (
                      <p className="text-xs text-[#666]">
                        Calories remaining: <span className="font-semibold text-[#4A90E2]">{Math.round(analysis.caloriesRemaining)}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={sendRecommendation}
                disabled={sending}
                className="w-full py-3 rounded-xl bg-[#4A90E2] text-white font-semibold hover:bg-[#3a7bc8] disabled:opacity-60"
              >
                {sending ? 'Sending…' : 'Send Recommendation to Client'}
              </button>
            </div>
          )}
        </div>
      )}
    </PremiumGate>
  );
}
